import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { ToastContext } from "@laerdal/life-react-components";
import { FailToastOptions } from "../../../model/constants/ToastConstants";
import CaseApi from "../../../services/api/CaseApi";
import { selectMarket } from "../../../store/slices/globalConfigSlice";
import { selectIsAuthenticated } from "../../../store/slices/userSlice";
import { marketList } from "../../../model/constants/marketContants";
import posthog from 'posthog-js';

export const useCaseSubmission = () => {
    const [creatingcase, setCreatingcase] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const { addToast } = useContext(ToastContext);
    const { t } = useTranslation("cases");
    const currentMartket = useSelector(selectMarket);
    const isAuthenticated = useSelector(selectIsAuthenticated);

    const buildDescription = (data: any, caseSubOption: any) => {
        if (caseSubOption?.value === 'revokeAhaLicense') {
            return `
i. Organization Name - ${data.organization}
ii. Platform - LLP
iii. Environment - Prod
iv. Organization ID (from platform) - ${data.llpOrganizationID}
v. Severity – P3
vi. Organization Type – ${data.organisationType}
Screen Shot attached: NO
Data File Attached: NO
Original Case Owner:  
Country of origin: ${data.country}

Product affected: ${data.product}
item Catalog number: 20-2802	
Component: Software
Sub-Component: AHA/HeartCode/NRP Software
Problem: Settings
Details: Request to Revoke
User entered description: ${data.description}
            `;
        }

        return `
                First name: ${data.firstName}
                Last name: ${data.lastName}
                Country: ${data.country}
                Telephone: ${data.telephone}
                Selected market: ${marketList[currentMartket].marketName}
                Language: ${marketList[currentMartket].language}
                howCanWeHelp: ${caseSubOption?.howCanWeHelp ?? 'Other'}
                ${data.option ? `Option: ${data.option}` : ''}
                ${data.suboption ? `Suboption: ${data.suboption}` : ''}
                ${data.contactMethod ? `Contact method: ${data.contactMethod}` : ''}
                ${data.orderOrPoNumber ? `Order and/or PO Numbe: ${data.orderOrPoNumber}` : ''}
                ${data.productSerial ? `Product Serial Number: ${data.productSerial}` : ''}
                ${data.stateOrProvince ? `State or province: ${data.stateOrProvince}` : ''}
                ${data.zipCode ? `Zip code: ${data.zipCode}` : ''}
                ${data.assistanceWith ? `Assistance with: ${data.assistanceWith}` : ''}
                ${data.productName ? `Product name: ${data.productName}` : ''}
                ${data.accountNumber ? `Account number: ${data.accountNumber}` : ''}
                ${data.reasonForReturn ? `Reason for return: ${data.reasonForReturn}` : ''}
                ${data.organisationType ? `Organisation type: ${data.organisationType}` : ''}
                ${data.role ? `Role: ${data.role}` : ''}

                Description: ${data.description}
            `;
    };

    const getCaseSubject = (caseSubOption: any, product: string) => {
        if (caseSubOption?.value === 'revokeAhaLicense') {
            if (product == 'E-Book') 
                return 'P3 – "Ebook" – Revoke';
            if (product == 'Course-URL') 
                return 'P3 – "Assignment" – Revoke';
        }
        return caseSubOption?.caseSubject ?? 'Other';
    };

    const submitCase = async (data: any, files: File[], option: string, suboption: string, caseSubOption: any, llpLicenseDate: Date) => {
        if (creatingcase) return;

        if (caseSubOption?.value === 'revokeAhaLicense') {
            const now = new Date();
            const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

            if (llpLicenseDate < startOfCurrentMonth) {
                setModalOpen(true);
                return;
            }
        }

        setCreatingcase(true);

        const description = buildDescription(data, caseSubOption);

        const caseDto = {
            customerNumber: data.accountNumber,
            country: data.country,
            email: data.workEmail,
            subject: getCaseSubject(caseSubOption, data.product),
            option: option,
            suboption: suboption,
            howCanWeHelp: caseSubOption?.howCanWeHelp ?? 'Other',
            caseType: caseSubOption?.caseType ?? 'Quick Case',
            caseSubtype: caseSubOption?.caseSubtype,
            orderOrPoNumber: data.orderOrPoNumber,
            serialNumber: data.productSerial,
            productLineCode: data.productLineCode,
            firstName: data.firstName,
            lastName: data.lastName,
            description: description.replace(/^\s*$(?:\r\n?|\n)/gm, ''),
            deflectionResponse: data.deflectionResponse || undefined,
        };

        try {
            const caseNumber = await CaseApi.CreateCase(caseDto, files);
            
            const posthogObject = {
                caseNumber: caseNumber,
                organization: data.organization,
                country: data.country,
                telephone: data.telephone,
                contactMethod: data.contactMethod,
                orderOrPoNumber: data.orderOrPoNumber,
                productSerial: data.productSerial,
                productLineCode: data.productLineCode,
                description: data.description,
                stateOrProvince: data.stateOrProvince,
                zipCode: data.zipCode,
                assistanceWith: data.assistanceWith,
                productName: data.productName,
                accountNumber: data.accountNumber,
                reasonForReturn: data.reasonForReturn,
                organisationType: data.organisationType,
                role: data.role,
                files: files?.map(file => file.name),
                isAuthenticated: isAuthenticated,
            };

            posthog?.capture?.('CreateCaseModal Created', posthogObject);
            posthog?.capture?.('SUPP-CreateCase Created', posthogObject);

            navigate(`/case/create/confirmation/${caseNumber}?hideAccountLink=${caseSubOption?.value == 'revokeAhaLicense'}`);
        } catch (ex: any) {
            if (ex?.response?.data?.errors?.hasOwnProperty('') &&
                ex?.response?.data?.errors['']?.some((error: string) => error?.includes('Request body too large'))) {
                posthog?.capture?.('CreateCaseModal documents too large', caseDto);
                addToast(t('The documents are too large! Maximum size of all documents must be under 20MB'), FailToastOptions);
            } else {
                posthog?.capture?.('CreateCaseModal Error', caseDto);
                addToast(t('There was a problem creating the case'), FailToastOptions);
            }
            setCreatingcase(false);
        }
    };

    return {
        creatingcase,
        modalOpen,
        setModalOpen,
        submitCase,
    };
};
